import { useCallback, useEffect, useState } from 'react';
import { Home, type HomeChoice } from './screens/Home';
import { LocalGame } from './local/LocalGame';
import { Solo } from './local/Solo';
import { RemoteGame } from './remote/RemoteGame';
import { AnnounceProvider } from './ui/shell';
import { normalizeCode } from '@shared/protocol';
import type { DeckId } from '@shared/content';

type Route =
  | { kind: 'home' }
  | { kind: 'local'; names: string[]; passes: 1 | 2 }
  | { kind: 'solo' }
  | { kind: 'remote'; code: string | null };

/** `#/ABCD` is the share link. Anything else is the front door. */
function routeFromHash(): Route {
  const code = normalizeCode(location.hash.replace(/^#\/?/, ''));
  return code.length === 4 ? { kind: 'remote', code } : { kind: 'home' };
}

export function App() {
  const [route, setRoute] = useState<Route>(routeFromHash);
  // Bumped when someone starts a fresh room from a dead one: the room client
  // holds a socket and a seat, so it has to be rebuilt rather than reused.
  const [roomEpoch, setRoomEpoch] = useState(0);
  // The mode outlives a single game: whoever picked Cape Town last night should
  // not have to find it again tonight.
  const [deck, setDeck] = useState<DeckId>(() => {
    try {
      return localStorage.getItem('needle:deck') === 'capetown' ? 'capetown' : 'normal';
    } catch {
      return 'normal';
    }
  });
  const chooseDeck = useCallback((next: DeckId) => {
    setDeck(next);
    try {
      localStorage.setItem('needle:deck', next);
    } catch {
      /* private mode: it just forgets */
    }
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const goHome = useCallback(() => {
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    setRoute({ kind: 'home' });
  }, []);

  const choose = useCallback((choice: HomeChoice) => {
    switch (choice.kind) {
      case 'local':
        setRoute({ kind: 'local', names: choice.names, passes: choice.passes });
        break;
      case 'solo':
        setRoute({ kind: 'solo' });
        break;
      case 'host':
        setRoute({ kind: 'remote', code: null });
        break;
      case 'join':
        setRoute({ kind: 'remote', code: choice.code });
        break;
    }
  }, []);

  return (
    <AnnounceProvider>
      <div className="app">
        {route.kind === 'home' && <Home onChoose={choose} deck={deck} onDeckChange={chooseDeck} />}
        {route.kind === 'local' && (
          <LocalGame names={route.names} passes={route.passes} deck={deck} onExit={goHome} />
        )}
        {route.kind === 'solo' && <Solo onExit={goHome} />}
        {route.kind === 'remote' && (
          <RemoteGame
            key={`${route.code ?? 'new'}:${roomEpoch}`}
            joinCode={route.code}
            deck={deck}
            onExit={goHome}
            onNewRoom={() => {
              if (location.hash) history.replaceState(null, '', location.pathname + location.search);
              setRoute({ kind: 'remote', code: null });
              setRoomEpoch((n) => n + 1);
            }}
          />
        )}
      </div>
    </AnnounceProvider>
  );
}
